import createReducer from 'lib/create-reducer'
import * as analytics from 'lib/analytics'
import { constants } from './actions'
import { constants as searchConstants } from 'store/search/actions'
import saga from './sagas'
import { getLocalStorage } from '../../lib/get-local-storage'
import { PLANS, SUBSCRIPTION_STATUS } from '../../../server/lib/constants'

export const defaultState = {
  referralPartner: null,
  freeReports: 0,

  bannerContent: null,
  bannerClosed: false,

  navMenu: {},

  createDashboardFromSearchResultsModalShow: false,
  savedSearchModalShow: false,
  searchFilterModalShow: false,
  flagModalShow: false,
  resetPasswordModalShow: false,
  loginModalShow: false,
  referenceCheckModalShow: false,
  deleteDashboardModalShow: false,
  addReportToDashboardModalShow: false,
  shareDashboardModalShow: false,
  editDashboardModalShow: false,
  editCollectionModalShow: false,
  upgradeModalShow: false,
  exportReportModalShow: false,
  exportReferenceModalShow: false,
  exportResultsModalShow: false,
  generateCitationModalShow: false,
  exportDashboardModalShow: false,
  exportStructuredOutputModalShow: false,
  dashboardLimitErrorModalShow: false,
  whatIsThisReportModalShow: false,
  helpModalShow: false,
  patentFilterModalShow: false,
  searchStrategyModalShow: false,
  trialOverModalShow: false,
  activateTrialModalShow: false,
  cancelTrialCheckoutModalShow: false,
  cookieModalShow: false,
  personaModalShow: false,
  mcpWelcomeModalShow: false,
  loggedOutModalShow: true,
  summarizeCitationsModalShow: false,
  modalFromContext: '',
  shownModals: [],

  hasUser: false,
  user: null,
  fetchingUser: false,
  deletingUser: false,
  savingUser: false,
  saveUserError: false,

  onboardingLoadTracker: {
    state: undefined
  },

  userEntitlements: null,
  entitlementsLoading: false,

  referralsLoading: false,

  pageError: null,

  browser: null,
  extensionLoaded: false,

  organizationLicense: {
    hasLicense: false,
    licenseChecked: false
  },
  linkResolver: null,

  sickoMode: false,

  authors: [],

  navOpen: false,

  showMobileSearch: false,

  applyDiscountLoading: false,
  applyDiscountError: null,
  applyDiscountSuccess: false,

  userDiscount: null,

  search: {
    term: '',
    focused: false,
    autocompletesLoadTracker: {},
    autocompletes: {},
    booleanSuggestions: {
      loading: false,
      error: null,
      variants: null,
      originalTerm: null,
      selectedVariant: null
    },
    // AI-generated boolean-search suggestions shown under the empty search
    // input, seeded from recent searches + Assistant chat titles. `items` holds
    // the parsed variants ([{ query, label, explanation }]); `key` is the seed
    // signature the current result was generated for.
    suggestedSearches: {
      loading: false,
      items: [],
      error: null,
      key: null
    }
  },

  referencesToExport: [],

  citeGeneratorPaperData: {},

  dimensions: {},

  cookiePreference: null,

  userChurnKey: null,
  churnKeyLoading: false,

  fullTextLinkState: {},
  fullTextLinkQueue: [],

  agAvailabilityState: {},
  agAvailabilityQueue: []
}

export const ui = createReducer(
  defaultState,
  {
    [constants.TOGGLE_MODAL]: (state, { name, to, from }) => {
      const { shownModals } = state
      const key = `${name}ModalShow`

      if (!(key in state)) {
        console.warn('Tried to toggle non-existant modal!')
        return state
      }

      const toggleTo = typeof to === 'boolean' ? to : !state[key]
      if (toggleTo) {
        analytics.modalView(name)
      }

      return {
        ...state,
        [key]: toggleTo,
        modalFromContext: from || state.modalFromContext,
        shownModals: toggleTo && !shownModals.includes(name) ? [...state.shownModals, name] : state.shownModals
      }
    },
    [constants.CLEAR_MODAL_FROM_CONTEXT]: state => ({
      ...state,
      modalFromContext: ''
    }),
    [constants.TOGGLE_NAV]: (state, navState) => ({
      ...state,
      navOpen: navState
    }),
    [constants.SET_USER]: (state, user) => ({
      ...state,
      hasUser: true,
      user
    }),
    [constants.SET_PAGE_ERROR]: (state, pageError) => ({
      ...state,
      pageError
    }),
    [constants.LOAD_BROWSER]: (state, browser) => ({
      ...state,
      browser
    }),
    [constants.HANDLE_REFERRAL]: (state, referrer) => {
      if (!referrer) {
        return state
      }

      const localStorage = getLocalStorage()
      const referralConfig =
        CONFIG.referrals.find(referral => {
          const usedReferral = localStorage?.getItem(`used-referral-partner-${referral.partner}`)
          if (usedReferral && usedReferral !== 'false') {
            return false
          }

          for (const s of referral.includes) {
            if (referrer.includes(s) && localStorage) {
              // if referral partner is an unlimited first click partner than we don't want to set the localStorage
              if (referral.unlimitedFirstClicks) {
                return true
              }

              localStorage.setItem(`used-referral-partner-${referral.partner}`, 'true')
              return true
            }
          }

          return false
        }) || {}

      // Remove reprints link resolver
      // from referral partner because we now use
      // check availability.
      return {
        ...state,
        referralPartner: referralConfig.partner || null,
        freeReports: referralConfig.reports || 0,
        linkResolver:
          referrer === 'reprintsdesk.com'
            ? null
            : {
                ...(referralConfig.linkResolver || state.linkResolver),
                referrerLocation: referrer
              }
      }
    },
    [constants.DOCK_FREE_REPORT]: state => ({
      ...state,
      freeReports: state.freeReports - 1
    }),
    [constants.LOAD_USER]: state => ({
      ...state,
      fetchingUser: true
    }),
    [constants.LOAD_USER_SUCCESS]: (state, user) => ({
      ...state,
      fetchingUser: false,
      user,
      hasUser: true
    }),
    [constants.LOAD_USER_FAILURE]: state => ({
      ...state,
      fetchingUser: false
    }),
    [constants.MERGE_USER_SUBSCRIPTION]: (state, subscription) => ({
      ...state,
      user: {
        ...(state.user || {}),
        subscription: {
          ...(state.user?.subscription || {}),
          plan: subscription.plan
        }
      }
    }),
    [constants.DELETE_USER]: state => ({
      ...state,
      deletingUser: true
    }),
    [constants.DELETE_USER_SUCCESS]: (state, user) => ({
      ...state,
      deletingUser: false,
      user: null
    }),
    [constants.DELETE_USER_FAILURE]: state => ({
      ...state,
      deletingUser: false
    }),
    [constants.SAVE_USER]: state => ({
      ...state,
      savingUser: true,
      saveUserError: false
    }),
    [constants.SAVE_USER_SUCCESS]: (state, user) => ({
      ...state,
      savingUser: false,
      saveUserError: false,
      user
    }),
    [constants.SAVE_USER_FAILURE]: (state, error) => ({
      ...state,
      savingUser: false,
      saveUserError: true
    }),
    [constants.LOGOUT_SUCCESS]: (state, user) => ({
      ...state,
      user: null,
      hasUser: false
    }),
    [constants.GET_ENTITLEMENTS]: state => ({
      ...state,
      entitlementsLoading: true
    }),
    [constants.GET_ENTITLEMENTS_SUCCESS]: (state, payload) => ({
      ...state,
      entitlementsLoading: false,
      userEntitlements: payload
    }),
    [constants.GET_ENTITLEMENTS_FAILURE]: state => ({
      ...state,
      entitlementsLoading: false,
      userEntitlements: {}
    }),
    [constants.SAVE_REFERRALS]: (state, { referrals }) => ({
      ...state,
      referralsLoading: true,
      user: {
        ...state.user,
        referred: [...new Set([...state.user.referred, ...referrals])]
      },
      saveReferralsError: false
    }),
    [constants.SAVE_REFERRALS_SUCCESS]: (state, payload) => ({
      ...state,
      referralsLoading: false,
      user: {
        ...state.user,
        referred: payload && payload.referred
      },
      saveReferralsError: false
    }),
    [constants.SAVE_REFERRALS_FAILURE]: state => ({
      ...state,
      referralsLoading: false,
      saveReferralsError: true
    }),
    [constants.EXTENSION_LOADED]: state => ({
      ...state,
      extensionLoaded: true
    }),
    [constants.SET_BANNER_CONTENT]: (state, bannerContent) => ({
      ...state,
      bannerContent
    }),
    [constants.SET_BANNER_CLOSED]: (state, bannerClosed) => ({
      ...state,
      bannerClosed
    }),
    [constants.CHECK_PREMIUM_IP_SUCCESS]: (state, organizationLicense) => {
      if (organizationLicense.hasLicense && organizationLicense.linkResolverURL) {
        const linkResolver = {
          text: organizationLicense.linkResolverImageURL
            ? null
            : `Check for full-text from ${organizationLicense.name}`,
          baseURL: organizationLicense.linkResolverURL,
          partnerName: organizationLicense.slug,
          imageURL: organizationLicense.linkResolverImageURL
        }
        return {
          ...state,
          organizationLicense,
          linkResolver
        }
      }
      return {
        ...state,
        organizationLicense
      }
    },
    [constants.CHECK_PREMIUM_IP_FAILURE]: state => ({
      ...state,
      // A failed check still resolves the license question (to "no license"),
      // so mark it checked — gates rely on `licenseChecked` to know the
      // entitlement is settled rather than still loading.
      organizationLicense: { hasLicense: false, licenseChecked: true }
    }),
    [constants.TOGGLE_MOBILE_SEARCH]: state => ({
      ...state,
      showMobileSearch: !state.showMobileSearch
    }),
    [constants.REENABLE_SUBSCRIPTION_SUCCESS]: (state, subscription) => ({
      ...state,
      user: {
        ...state.user,
        subscription: {
          ...state.user.subscription,
          ...subscription
        }
      }
    }),
    [constants.APPLY_DISCOUNT]: state => ({
      ...state,
      applyDiscountLoading: true,
      applyDiscountSuccess: false,
      applyDiscountError: null
    }),
    [constants.APPLY_DISCOUNT_ERROR]: (state, payload, error) => ({
      ...state,
      applyDiscountLoading: false,
      applyDiscountSuccess: false,
      applyDiscountError:
        payload ||
        error?.response?.data?.error ||
        'An error occurred applying discount please try again or contact us customersupport@researchsolutions.com.'
    }),
    [constants.APPLY_DISCOUNT_SUCCESS]: state => ({
      ...state,
      applyDiscountLoading: false,
      applyDiscountSuccess: true,
      applyDiscountError: null
    }),
    [constants.FETCH_DISCOUNT]: state => ({
      ...state,
      userDiscount: null
    }),
    [constants.FETCH_DISCOUNT_SUCCESS]: (state, payload) => ({
      ...state,
      userDiscount: payload
    }),
    [constants.FETCH_DISCOUNT_ERROR]: state => ({
      ...state,
      userDiscount: null
    }),
    [constants.SET_HAS_USER]: (state, payload) => ({
      ...state,
      hasUser: payload
    }),
    [constants.SET_SEARCH_FOCUSED]: (state, focused) => ({
      ...state,
      search: {
        ...state.search,
        focused
      }
    }),
    [constants.SET_SEARCH_TERM]: (state, term) => {
      const newTerm = term || ''
      const suggestions = state.search.booleanSuggestions
      // Active variants outlive only the term they were generated for.
      const shouldClearActive = suggestions && suggestions.originalTerm !== null && suggestions.originalTerm !== newTerm
      // The post-pick cache survives only as long as the input still matches
      // the chosen variant. Once the user edits away, we drop the cache.
      const shouldClearSelected =
        suggestions && suggestions.selectedVariant !== null && suggestions.selectedVariant.query !== newTerm
      if (!shouldClearActive && !shouldClearSelected) {
        return {
          ...state,
          search: { ...state.search, term: newTerm }
        }
      }
      return {
        ...state,
        search: {
          ...state.search,
          term: newTerm,
          booleanSuggestions: {
            loading: shouldClearActive ? false : suggestions.loading,
            error: shouldClearActive ? null : suggestions.error,
            variants: shouldClearActive ? null : suggestions.variants,
            originalTerm: shouldClearActive ? null : suggestions.originalTerm,
            selectedVariant: shouldClearSelected ? null : suggestions.selectedVariant
          }
        }
      }
    },
    [constants.GENERATE_BOOLEAN_SEARCH]: (state, { term }) => ({
      ...state,
      search: {
        ...state.search,
        booleanSuggestions: {
          loading: true,
          error: null,
          variants: null,
          originalTerm: term,
          // A fresh generation supersedes any prior pick.
          selectedVariant: null
        }
      }
    }),
    [constants.GENERATE_BOOLEAN_SEARCH_SUCCESS]: (state, { variants, originalTerm }) => ({
      ...state,
      search: {
        ...state.search,
        booleanSuggestions: {
          ...state.search.booleanSuggestions,
          loading: false,
          error: null,
          variants,
          originalTerm
        }
      }
    }),
    [constants.GENERATE_BOOLEAN_SEARCH_FAILURE]: (state, payload) => ({
      ...state,
      search: {
        ...state.search,
        booleanSuggestions: {
          ...state.search.booleanSuggestions,
          loading: false,
          variants: null,
          error: payload?.error || 'Unable to generate boolean suggestions.'
        }
      }
    }),
    [constants.CLEAR_BOOLEAN_SEARCH_SUGGESTIONS]: state => ({
      ...state,
      search: {
        ...state.search,
        booleanSuggestions: {
          loading: false,
          error: null,
          variants: null,
          originalTerm: null,
          // "Use original query" / explicit dismissal also drops the cache.
          selectedVariant: null
        }
      }
    }),
    [constants.SELECT_BOOLEAN_VARIANT]: (state, { query, originalTerm, variants }) => ({
      ...state,
      search: {
        ...state.search,
        booleanSuggestions: {
          loading: false,
          error: null,
          // Active variants close out; the snapshot moves into selectedVariant
          // so we can offer "Choose a different AI-generated query" on reopen.
          variants: null,
          originalTerm: null,
          selectedVariant: { query, originalTerm, variants }
        }
      }
    }),
    [constants.RESET_ACTIVE_BOOLEAN_SUGGESTIONS]: state => ({
      // Fired when the dropdown closes, so the next open starts in State A.
      // Preserves selectedVariant so the "Choose a different…" CTA still works.
      ...state,
      search: {
        ...state.search,
        booleanSuggestions: {
          ...state.search.booleanSuggestions,
          loading: false,
          error: null,
          variants: null,
          originalTerm: null
        }
      }
    }),
    [constants.REOPEN_BOOLEAN_SUGGESTIONS]: state => {
      const cached = state.search.booleanSuggestions?.selectedVariant
      if (!cached) return state
      return {
        ...state,
        search: {
          ...state.search,
          booleanSuggestions: {
            ...state.search.booleanSuggestions,
            loading: false,
            error: null,
            variants: cached.variants,
            originalTerm: cached.originalTerm
          }
        }
      }
    },
    [constants.GENERATE_SUGGESTED_SEARCHES]: (state, { key }) => ({
      ...state,
      search: {
        ...state.search,
        suggestedSearches: { loading: true, items: [], error: null, key }
      }
    }),
    [constants.GENERATE_SUGGESTED_SEARCHES_SUCCESS]: (state, { items, key }) => ({
      ...state,
      search: {
        ...state.search,
        suggestedSearches: { loading: false, items: items || [], error: null, key }
      }
    }),
    [constants.GENERATE_SUGGESTED_SEARCHES_FAILURE]: (state, { error, key }) => ({
      ...state,
      search: {
        ...state.search,
        suggestedSearches: { loading: false, items: [], error, key }
      }
    }),
    [constants.FETCH_AUTOCOMPLETES]: (state, { field }) => ({
      ...state,
      search: {
        ...state.search,
        autocompletesLoadTracker: {
          ...state.search.autocompletesLoadTracker,
          [field]: {
            ...state.search.autocompletesLoadTracker[field],
            state: 'loading'
          }
        }
      }
    }),
    [constants.FETCH_AUTOCOMPLETES_SUCCESS]: (state, { field, autocompletes }) => {
      const nextAutocompletes = {
        ...state.search.autocompletes,
        [field]: autocompletes
      }
      const autocompletesLoadTracker = {
        ...state.search.autocompletesLoadTracker,
        [field]: {
          ...state.search.autocompletesLoadTracker[field],
          state: 'loaded'
        }
      }

      return {
        ...state,
        search: {
          ...state.search,
          autocompletes: nextAutocompletes,
          autocompletesLoadTracker
        }
      }
    },
    [constants.FLUSH_AUTOCOMPLETES]: (state, key) => ({
      ...state,
      search: {
        ...state.search,
        autocompletes: {
          ...state.search.autocompletes,
          [key]: []
        }
      }
    }),
    [searchConstants.HANDLE_QUERY_CHANGE]: (state, query) => ({
      ...state,
      search: {
        ...state.search,
        term: query.q || ''
      }
    }),
    [searchConstants.CLEAR_FILTERS]: (state, query) => ({
      ...state,
      search: {
        ...defaultState.search,
        term: state.search.term
      }
    }),
    [constants.FETCH_ONBOARDING]: state => ({
      ...state,
      onboardingLoadTracker: {
        state: 'loading'
      }
    }),
    [constants.FETCH_ONBOARDING_SUCCESS]: (state, onboarding) => ({
      ...state,
      onboardingLoadTracker: {
        state: 'success'
      },
      user: {
        ...state.user,
        onboarding
      }
    }),
    [constants.FETCH_ONBOARDING_FAILURE]: state => ({
      ...state,
      onboardingLoadTracker: {
        state: 'error'
      }
    }),
    [constants.FLUSH_ONBOARDING_LOADED]: state => ({
      ...state,
      onboardingLoadTracker: {
        state: undefined
      }
    }),
    [constants.SET_REFERENCES_TO_EXPORT]: (state, dois) => ({
      ...state,
      referencesToExport: dois
    }),
    [constants.SET_CITE_GENERATOR_PAPER_DATA]: (state, { citeGeneratorPaperData }) => ({
      ...state,
      citeGeneratorPaperData: {
        ...citeGeneratorPaperData
      },
      referencesToExport: citeGeneratorPaperData ? [citeGeneratorPaperData.doi] : []
    }),
    [constants.UPDATE_DIMENSIONS]: (state, { name, dimensions }) => ({
      ...state,
      dimensions: {
        ...state.dimensions,
        [name]: dimensions
      }
    }),
    [constants.TRIAL_ACTIVATED]: state => ({
      ...state
    }),
    [constants.TRIAL_ACTIVATED_SUCCESS]: state => ({
      ...state,
      user: {
        ...state.user,
        subscription: {
          ...(state.user?.subscription || {}),
          plan: PLANS.premium,
          status: SUBSCRIPTION_STATUS.trialing
        },
        activatedTrial: true
      }
    }),
    [constants.TRIAL_ACTIVATED_FAILURE]: state => ({
      ...state
    }),
    [constants.SET_COOKIE_PREFERENCE]: (state, cookiePreference) => ({
      ...state,
      cookiePreference
    }),
    [constants.FETCH_CHURN_KEY]: state => ({
      ...state,
      userChurnKey: null,
      churnKeyLoading: true
    }),
    [constants.FETCH_CHURN_KEY_SUCCESS]: (state, { churnKeyHash }) => ({
      ...state,
      userChurnKey: churnKeyHash,
      churnKeyLoading: false
    }),
    [constants.FETCH_CHURN_KEY_FAILURE]: state => ({
      ...state,
      userChurnKey: null,
      churnKeyLoading: false
    }),

    // Full Text Fetch
    [constants.FETCH_FULL_TEXT_LINK]: (state, { doi, memberId }) => ({
      ...state,
      fullTextLinkState: {
        ...state.fullTextLinkState,
        [doi]: { loading: true, doi, memberId }
      },
      fullTextLinkQueue: [...state.fullTextLinkQueue, { doi, memberId }]
    }),
    [constants.FULL_TEXT_QUEUE_CLEAR]: state => ({
      ...state,
      fullTextLinkQueue: []
    }),
    [constants.FETCH_FULL_TEXT_LINK_SUCCESS]: (state, payload) => {
      const { fullTextLinkState } = state
      const existingLen = Object.keys(payload).length
      const MAX = 100

      const limitedExisting =
        Object.keys(fullTextLinkState).length + existingLen > MAX
          ? Object.fromEntries(Object.entries(fullTextLinkState).slice(-MAX + existingLen))
          : fullTextLinkState

      return {
        ...state,
        fullTextLinkState: {
          ...limitedExisting,
          ...payload
        }
      }
    },
    [constants.FETCH_FULL_TEXT_LINK_FAILURE]: (state, payload) => ({
      ...state,
      fullTextLinkState: payload.map(({ doi, memberId }) => ({
        loading: false,
        linkText: 'View full text',
        linkUrl: 'https://doi.org/' + doi,
        source: 'publisher_version_of_record'
      }))
    }),

    // AG Availability Fetch
    [constants.AVAILABILITY_QUEUE_APPEND]: (state, { doi }) => ({
      ...state,
      agAvailabilityQueue: [...state.agAvailabilityQueue, doi]
    }),
    [constants.AVAILABILITY_QUEUE_CLEAR]: state => ({
      ...state,
      agAvailabilityQueue: []
    }),
    [constants.AVAILABILITY_QUEUE_SUCCESS]: (state, payload) => {
      const { agAvailabilityState } = state
      const existingLen = Object.keys(payload).length
      const MAX = 100

      const limitedExisting =
        Object.keys(agAvailabilityState).length + existingLen > MAX
          ? Object.fromEntries(Object.entries(agAvailabilityState).slice(-MAX + existingLen))
          : agAvailabilityState

      return {
        ...state,
        agAvailabilityState: {
          ...limitedExisting,
          ...payload
        }
      }
    },

    [constants.UPDATE_ACCESS_LOG]: state => ({
      ...state
    })
  },
  saga
)

export default ui
